<script setup lang="ts">
import { ref, watch, Ref } from "vue";
import { debounce } from "vue-debounce";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import useDialog from "@/ui/composables/useDialog";
import AddEditSizeDialog from "@/modules/campaign_planner/components/screens/AddEditSizeDialog.vue";
import SizeDO = Modules.CampaignPlanner.Data.SizeDO;

type StringFilter = string | null;

interface TableOptions {
	page: number;
	itemsPerPage: number;
	sortBy: { key: string; order: "asc" | "desc" }[];
}

const headers = [
	{ title: "ID", key: "id", align: "start", sortable: true },
	{ title: "Label", key: "label", align: "start", sortable: true },
	{ title: "Orientation", key: "orientation", align: "start", sortable: true },
	{ title: "Inventory", key: "inventory_type", align: "start", sortable: true },
	{ title: "Location", key: "location", align: "start", sortable: true },
	{ title: "Screens", key: "screens_count", align: "center", sortable: false },
	{
		title: "Actions",
		key: "actions",
		align: "end",
		sortable: false,
		width: 100,
	},
] as const;

const sizes: Ref<SizeDO[]> = ref([]);
const loading: Ref<boolean | string> = ref(false);
const total = ref(0);
const search = ref("");
const filterLabel = ref("");
const tableOptions = ref<TableOptions>({
	page: 1,
	itemsPerPage: 25,
	sortBy: [],
});

const filters = ref<{
	orientation: StringFilter;
	inventory_type: StringFilter;
	location: StringFilter;
}>({
	orientation: null,
	inventory_type: null,
	location: null,
});

const isDialogVisible = ref(false);
const sizeToEdit = ref<SizeDO | null>(null);

watch(filterLabel, () => (search.value = String(Date.now())));
watch(filters, () => (search.value = String(Date.now())), { deep: true });

const buildFilterParams = (): Record<string, string> => {
	const params: Record<string, string> = {};

	for (const [key, value] of Object.entries(filters.value)) {
		if (value !== null) params[`filter[${key}]`] = value;
	}

	if (filterLabel.value) params["filter[label]"] = filterLabel.value;

	return params;
};

const loadSizes = debounce(async (options: TableOptions) => {
	tableOptions.value = options;
	loading.value = "secondary";

	const { page, itemsPerPage, sortBy } = options;
	const sort = sortBy[0]
		? (sortBy[0].order === "desc" ? "-" : "") + sortBy[0].key
		: undefined;

	const response = await useApiClient("campaignPlanner").getSizes({
		page,
		per_page: itemsPerPage === -1 ? undefined : itemsPerPage,
		sort,
		...buildFilterParams(),
	});

	if (response.isOk()) {
		sizes.value = response.value.data;
		total.value = response.value.total;
	}

	loading.value = false;
}, 300);

const openAdd = () => {
	sizeToEdit.value = null;
	isDialogVisible.value = true;
};

const openEdit = (size: SizeDO) => {
	sizeToEdit.value = size;
	isDialogVisible.value = true;
};

const deleteSize = async (size: SizeDO) => {
	const confirmed = await useDialog().confirm({
		title: "Delete size",
		message: `Are you sure you want to delete "${size.label}"? This will unassign all screens linked to this size.`,
		isDelete: true,
	});

	if (!confirmed) return;

	const response = await toastApiResult(
		useApiClient("campaignPlanner").deleteSize(size.id!)
	);

	if (response.isOk()) {
		loadSizes(tableOptions.value);
	}
};
</script>

<template>
	<add-edit-size-dialog
		v-if="isDialogVisible"
		v-model="isDialogVisible"
		:size-to-edit="sizeToEdit"
		@update-list="loadSizes(tableOptions)"
	/>

	<v-card elevation="1" class="mt-5">
		<div class="card-header">
			<v-text-field
				v-model="filterLabel"
				prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
				placeholder="Search sizes…"
				clearable
				hide-details
				variant="outlined"
				density="compact"
			/>

			<div class="filter-strip">
				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Orientation</span>
					<v-btn-toggle
						v-model="filters.orientation"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn value="Landscape" size="small">Landscape</v-btn>
						<v-btn value="Portrait" size="small">Portrait</v-btn>
					</v-btn-toggle>
				</div>

				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Inventory</span>
					<v-btn-toggle
						v-model="filters.inventory_type"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn value="Digital" size="small">Digital</v-btn>
						<v-btn value="Static" size="small">Static</v-btn>
					</v-btn-toggle>
				</div>

				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Location</span>
					<v-btn-toggle
						v-model="filters.location"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn value="Road" size="small">Road</v-btn>
						<v-btn value="City" size="small">City</v-btn>
					</v-btn-toggle>
				</div>

				<div class="filter-group align-self-end ml-auto">
					<v-btn
						color="primary"
						size="small"
						prepend-icon="fa: fa-regular fa-plus"
						@click="openAdd"
					>
						Add size
					</v-btn>
				</div>
			</div>
		</div>

		<v-divider />

		<v-data-table-server
			:headers="headers"
			:items="sizes"
			:items-per-page="tableOptions.itemsPerPage"
			:page="tableOptions.page"
			:items-length="total"
			:loading="loading"
			:search="search"
			:multi-sort="false"
			class="elevation-0"
			@update:options="loadSizes"
		>
			<template v-slot:item.orientation="{ item }">
				<v-chip size="small" variant="tonal">{{ item.orientation }}</v-chip>
			</template>

			<template v-slot:item.inventory_type="{ item }">
				<v-chip size="small" variant="tonal">{{ item.inventory_type }}</v-chip>
			</template>

			<template v-slot:item.location="{ item }">
				<v-chip size="small" variant="tonal">{{ item.location }}</v-chip>
			</template>

			<template v-slot:item.screens_count="{ item }">
				<v-chip
					size="small"
					:color="item.screens?.length ? 'primary' : undefined"
					variant="tonal"
				>
					{{ item.screens?.length ?? 0 }}
				</v-chip>
			</template>

			<template v-slot:item.actions="{ item }">
				<v-btn
					size="x-small"
					icon="fa: fa-regular fa-pen"
					color="primary"
					variant="text"
					@click="openEdit(item)"
				/>
				<v-btn
					size="x-small"
					icon="fa: fa-regular fa-trash"
					color="error"
					variant="text"
					@click="deleteSize(item)"
				/>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<style scoped lang="scss">
.card-header {
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.filter-strip {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 20px;
	align-items: flex-end;
}

.filter-group {
	display: flex;
	flex-direction: column;
	gap: 4px;
}
</style>
